import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link2, X, Search, ExternalLink, Building2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClientStore } from '@/lib/stores/clientStore';
import { useMetaMessagingStore, type MetaConversation } from '@/lib/stores/metaMessagingStore';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { CreateClientModal } from '@/components/clients/CreateClientModal';
import { tip } from '@/components/messages/ChatTooltip';
import { useChatLeadPrefill } from '@/components/whatsapp2/shared/useChatLeadPrefill';
import { conversationLabel } from './metaConversationHelpers';

interface MetaClientLinkProps {
  conversation: MetaConversation;
  canEdit: boolean;
}

/**
 * Aggancio manuale della conversazione a un lead o cliente (stessa forma del
 * badge cliente delle chat WhatsApp). Nessun aggancio automatico: Instagram e
 * Messenger non espongono il telefono e il nome non basta.
 * "Crea nuovo Lead": modulo compilato con nome dal profilo e fonte del canale; il
 * Lead creato viene collegato alla conversazione con la stessa RPC (linkClient).
 */
export function MetaClientLink({ conversation, canEdit }: MetaClientLinkProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const clients = useClientStore((s) => s.clients);
  const leads = useClientStore((s) => s.leads);
  const fetchClients = useClientStore((s) => s.fetchClients);
  const fetchLeads = useClientStore((s) => s.fetchLeads);
  const linkClient = useMetaMessagingStore((s) => s.linkClient);
  const { canCreateLead } = usePermissions();
  const prefillLead = useChatLeadPrefill();
  const [createData, setCreateData] = useState<Record<string, string> | null>(null);

  const openCreate = async () => {
    setOpen(false);
    setSearch('');
    setCreateData(await prefillLead({
      channel: conversation.channel,
      name: conversation.contact_name || (conversation.profile_username ? `@${conversation.profile_username}` : conversationLabel(conversation)),
      instagramUsername: conversation.profile_username,
    }));
  };

  useEffect(() => {
    if (clients.length === 0) fetchClients();
    if (leads.length === 0) fetchLeads();
  }, [clients.length, leads.length, fetchClients, fetchLeads]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const entities = useMemo(() => {
    const map = new Map<string, (typeof clients)[0]>();
    for (const c of clients) map.set(c.id, c);
    for (const l of leads) if (!map.has(l.id)) map.set(l.id, l);
    return Array.from(map.values());
  }, [clients, leads]);

  const linked = conversation.client_id ? entities.find((e) => e.id === conversation.client_id) : null;

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entities.slice(0, 20);
    return entities
      .filter((c) => c.business_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q))
      .slice(0, 20);
  }, [entities, search]);

  if (conversation.client_id) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[rgba(35,131,226,0.08)] text-xs text-[#2383e2]">
        <Building2 className="h-3 w-3" />
        <button type="button" onClick={() => navigate(`/clients/${conversation.client_id}`)} className="hover:underline inline-flex items-center gap-1">
          {linked?.business_name ?? 'Cliente collegato'}
          <ExternalLink className="h-3 w-3" />
        </button>
        {canEdit && (
          <button type="button" onClick={() => void linkClient(conversation.id, null)} aria-label="Scollega" className="ml-1 hover:text-red-600">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  if (!canEdit) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors"
      >
        <Link2 className="h-3 w-3" />
        Collega Lead/Cliente
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-72 rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#202020] shadow-lg">
          <div className="p-2 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a4a4a0]" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email o telefono"
              className="w-full pl-7 pr-2 py-1.5 text-sm rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 focus:outline-none text-[#37352F] dark:text-white"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {results.length === 0 && <p className="px-3 py-2 text-xs text-[#787774]">Nessun risultato</p>}
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={async () => {
                  const ok = await linkClient(conversation.id, c.id);
                  if (ok) {
                    setOpen(false);
                    setSearch('');
                  }
                }}
                className="w-full text-left px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10"
              >
                <span className="block truncate">{c.business_name}</span>
                {c.email && <span className="block text-[11px] text-[#a4a4a0] truncate">{c.email}</span>}
              </button>
            ))}
          </div>
          {canCreateLead && (
            <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 p-1">
              <button
                type="button"
                onClick={() => void openCreate()}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10"
                {...tip(`Nome dal profilo e fonte ${conversation.channel === 'instagram' ? 'Instagram' : 'Messenger'} gia' compilati; il Lead viene collegato a questa conversazione`)}
              >
                <Plus className="h-3.5 w-3.5 text-[#2383E2]" />
                <span className="text-xs font-medium text-[#2383E2]">Crea nuovo Lead</span>
              </button>
            </div>
          )}
        </div>
      )}
      {createData && (
        <CreateClientModal
          isOpen
          onClose={() => setCreateData(null)}
          initialData={createData}
          isLead
          onClientCreated={async (id) => {
            setCreateData(null);
            await linkClient(conversation.id, id);
            void fetchLeads();
          }}
        />
      )}
    </div>
  );
}
